import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PhoneOutgoing, Copy, Loader2, QrCode, Layers, ArrowLeft, ArrowRight, Building2, FileDown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import QRCode from "react-qr-code";
import { exportSitePdf, exportSinglePdf } from "../../lib/exportPdf";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = { number: "", label: "", kind: "tenant", site_id: "", server_id: "", assigned_user_id: "", unlock_url: "", dtmf_digit: "", dtmf_type: "rfc2833" };

export function ExtensionsTab() {
  const [extensions, setExtensions] = useState([]);
  const [sites, setSites] = useState([]);
  const [servers, setServers] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [ringing, setRinging] = useState(null);
  const [qrExt, setQrExt] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({ start: "", end: "", label_pattern: "Unit {n}", site_id: "", server_id: "" });
  const [selectedSite, setSelectedSite] = useState(null); // null = site overview, site id, or "unassigned"

  const load = () => {
    api.get("/admin/extensions").then((res) => setExtensions(res.data)).catch(() => {});
    api.get("/admin/sites").then((res) => setSites(res.data)).catch(() => {});
    api.get("/admin/servers").then((res) => setServers(res.data)).catch(() => {});
    api.get("/admin/users").then((res) => setUsers(res.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const siteExts = selectedSite === null ? []
    : selectedSite === "unassigned"
    ? extensions.filter((e) => !e.site_id || !sites.some((s) => s.id === e.site_id))
    : extensions.filter((e) => e.site_id === selectedSite);

  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || "—";
  const userName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name || u.username : "—";
  };

  const serverOf = (id) => {
    const s = servers.find((x) => x.id === id);
    return s ? `${s.domain}:${s.port}` : "";
  };

  const qrValue = (e) => {
    const s = servers.find((x) => x.id === e.server_id);
    const domain = s ? s.domain : "sip.local";
    const transport = (s?.transport || "UDP").toLowerCase().replace("wss", "udp").replace("ws", "udp");
    return `sip:${e.sip_endpoint || e.number}@${domain};transport=${transport}`;
  };

  const submitBatch = async (ev) => {
    ev.preventDefault();
    try {
      const res = await api.post("/admin/extensions/batch", {
        start: Number(batchForm.start), end: Number(batchForm.end),
        label_pattern: batchForm.label_pattern,
        site_id: batchForm.site_id, server_id: batchForm.server_id,
      });
      toast.success(`Created ${res.data.created} extensions${res.data.skipped.length ? ` — skipped existing: ${res.data.skipped.join(", ")}` : ""}`);
      setBatchOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      site_id: selectedSite && selectedSite !== "unassigned" ? selectedSite : "",
      server_id: (servers.find((s) => s.site_id === selectedSite) || {}).id || "",
    });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      number: e.number, label: e.label, kind: e.kind,
      site_id: e.site_id || "", server_id: e.server_id || "", assigned_user_id: e.assigned_user_id || "",
      unlock_url: e.unlock_url || "",
      dtmf_digit: e.dtmf_digit || "",
      dtmf_type: e.dtmf_type || "rfc2833",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, assigned_user_id: form.assigned_user_id || null };
    try {
      if (editing) await api.put(`/admin/extensions/${editing.id}`, payload);
      else await api.post("/admin/extensions", payload);
      toast.success(editing ? "Extension updated" : "Extension created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeSite = async (ev, s) => {
    ev.stopPropagation();
    const count = extensions.filter((e) => e.site_id === s.id).length;
    if (!window.confirm(`Delete site "${s.name}"? Its ${count} extension(s) and linked servers will become unassigned.`)) return;
    try {
      await api.delete(`/admin/sites/${s.id}`);
      toast.success(`Site "${s.name}" deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pdfSite = (ev, s) => {
    ev.stopPropagation();
    const exts = extensions.filter((e) => e.site_id === s.id);
    if (!exts.length) {
      toast.error(`No extensions on "${s.name}" to export`);
      return;
    }
    exportSitePdf(s, exts, servers);
    toast.success(`PDF exported — ${exts.length} extension(s) for ${s.name}`);
  };

  const remove = async (e) => {
    try {
      await api.delete(`/admin/extensions/${e.id}`);
      toast.success("Extension deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const ring = async (e) => {
    setRinging(e.id);
    try {
      const res = await api.post(`/admin/extensions/${e.id}/ring`);
      if (res.data.delivered) toast.success(`Ringing extension ${e.number}…`);
      else toast.warning(`Extension ${e.number} is offline — call logged as missed`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRinging(null);
    }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  return (
    <div data-testid="extensions-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {selectedSite !== null && (
            <Button data-testid="back-to-sites-btn" variant="ghost" size="sm" onClick={() => setSelectedSite(null)}
              className="text-slate-400 hover:text-slate-100 px-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> All Sites
            </Button>
          )}
          <h3 className="text-lg font-semibold text-slate-100" data-testid="extensions-title">
            {selectedSite === null
              ? "Extensions by Site"
              : selectedSite === "unassigned"
              ? "Unassigned Extensions"
              : `${nameOf(sites, selectedSite)} — Extensions`}
          </h3>
        </div>
        <div className="flex gap-2">
          <Button data-testid="batch-create-btn" size="sm" variant="outline"
            onClick={() => {
              setBatchForm({ ...batchForm, site_id: selectedSite && selectedSite !== "unassigned" ? selectedSite : batchForm.site_id });
              setBatchOpen(true);
            }}
            className="border-[#1E2638] bg-transparent hover:bg-[#1E2638] text-slate-300">
            <Layers className="w-4 h-4 mr-1.5" /> Batch Create
          </Button>
          <Button data-testid="create-extension-btn" onClick={openCreate} size="sm"
            className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> New Extension
          </Button>
        </div>
      </div>

      {selectedSite === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="extension-sites-grid">
          {sites.map((s) => {
            const exts = extensions.filter((e) => e.site_id === s.id);
            const tenants = exts.filter((e) => e.kind === "tenant").length;
            const intercoms = exts.filter((e) => e.kind === "intercom").length;
            return (
              <button key={s.id} data-testid={`ext-site-card-${s.id}`} onClick={() => setSelectedSite(s.id)}
                className="ops-card p-5 text-left hover:border-emerald-500/40 transition-colors group">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="font-semibold text-slate-100 group-hover:text-emerald-400 transition-colors">{s.name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`ext-site-pdf-${s.id}`} title="Export credentials PDF for this site"
                      onClick={(ev) => pdfSite(ev, s)}
                      className="p-1.5 text-slate-600 hover:text-emerald-400 transition-colors">
                      <FileDown className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`ext-site-delete-${s.id}`} title="Delete site"
                      onClick={(ev) => removeSite(ev, s)}
                      className="p-1.5 text-slate-600 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                  </div>
                </div>
                <div className="flex gap-4 text-xs font-mono text-slate-400">
                  <span data-testid={`ext-site-count-${s.id}`} className="text-emerald-400">{exts.length} EXT</span>
                  <span>{tenants} TENANT</span>
                  <span>{intercoms} INTERCOM</span>
                </div>
                {s.address && <div className="text-xs text-slate-500 mt-2">{s.address}</div>}
              </button>
            );
          })}
          {extensions.some((e) => !e.site_id || !sites.some((s) => s.id === e.site_id)) && (
            <button data-testid="ext-site-card-unassigned" onClick={() => setSelectedSite("unassigned")}
              className="ops-card p-5 text-left hover:border-amber-500/40 transition-colors group border-dashed">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-400 group-hover:text-amber-400 transition-colors">Unassigned</div>
                <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition-colors" />
              </div>
              <div className="text-xs font-mono text-slate-500">
                {extensions.filter((e) => !e.site_id || !sites.some((s) => s.id === e.site_id)).length} EXT without a site
              </div>
            </button>
          )}
          {sites.length === 0 && extensions.length === 0 && (
            <div className="ops-card p-8 text-center text-slate-500 text-sm col-span-full" data-testid="extensions-empty">
              No sites or extensions yet. Create a site first, then add extensions to it.
            </div>
          )}
        </div>
      ) : (
      <div className="ops-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="extensions-table">
            <thead>
              <tr className="text-left ops-caption border-b border-[#1E2638] bg-[#0A0D14]">
                <th className="px-4 py-3">Ext</th>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Server / Site</th>
                <th className="px-4 py-3">Assigned To</th>
                <th className="px-4 py-3">Presence</th>
                <th className="px-4 py-3">Credentials</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {siteExts.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500" data-testid="extensions-empty">No extensions on this site yet — create one above.</td></tr>
              )}
              {siteExts.map((e) => (
                <tr key={e.id} className="border-b border-[#1E2638]/50 last:border-0 hover:bg-[#0A0D14]/60" data-testid={`extension-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-emerald-400">
                    {e.number}
                    {e.sip_endpoint && e.sip_endpoint !== e.number && (
                      <div className="text-[10px] font-normal text-amber-400/80 mt-0.5" data-testid={`sip-username-${e.id}`}>user: {e.sip_endpoint}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-100">{e.label}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
                      e.kind === "intercom"
                        ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
                        : "text-slate-300 bg-slate-500/10 border-slate-500/30"}`}>
                      {e.kind.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    <div className="font-mono">{nameOf(servers, e.server_id)}</div>
                    <div>{nameOf(sites, e.site_id)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{e.kind === "intercom" ? "—" : userName(e.assigned_user_id)}</td>
                  <td className="px-4 py-3">
                    {e.kind === "intercom" ? (
                      <span className="text-xs font-mono text-slate-500">DEVICE</span>
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${e.online ? "text-emerald-400" : "text-slate-500"}`}
                        data-testid={`presence-${e.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${e.online ? "bg-emerald-400 pulse-dot" : "bg-slate-600"}`} />
                        {e.online ? "ONLINE" : "OFFLINE"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button data-testid={`copy-secret-${e.id}`} onClick={() => copy(e.sip_secret, "SIP secret")}
                        className="text-xs font-mono text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1">
                        secret <Copy className="w-3 h-3" />
                      </button>
                      {e.kind === "tenant" && (
                        <button data-testid={`copy-username-${e.id}`} onClick={() => copy(e.sip_endpoint || e.number, "SIP username")} title="SIP username — register the app with this"
                          className="text-xs font-mono text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1">
                          user <Copy className="w-3 h-3" />
                        </button>
                      )}
                      {e.kind === "tenant" && (
                        <button data-testid={`qr-ext-${e.id}`} onClick={() => setQrExt(e)} title="Softphone QR provisioning"
                          className="text-xs font-mono text-slate-400 hover:text-cyan-400 inline-flex items-center gap-1">
                          qr <QrCode className="w-3 h-3" />
                        </button>
                      )}
                      {e.api_key && (
                        <button data-testid={`copy-apikey-${e.id}`} onClick={() => copy(e.api_key, "Device API key")}
                          className="text-xs font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                          api key <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {e.kind === "tenant" && (
                      <Button data-testid={`ring-extension-${e.id}`} variant="ghost" size="icon" onClick={() => ring(e)}
                        disabled={ringing === e.id} title="Test ring"
                        className="h-8 w-8 text-emerald-400 hover:text-emerald-300">
                        {ringing === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneOutgoing className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                    <Button data-testid={`pdf-extension-${e.id}`} variant="ghost" size="icon" title="Download credentials PDF"
                      onClick={() => { exportSinglePdf(e, sites, servers); toast.success(`PDF exported for ext ${e.number}`); }}
                      className="h-8 w-8 text-slate-400 hover:text-emerald-400"><FileDown className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`edit-extension-${e.id}`} variant="ghost" size="icon" onClick={() => openEdit(e)}
                      className="h-8 w-8 text-slate-400 hover:text-slate-100"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`delete-extension-${e.id}`} variant="ghost" size="icon" onClick={() => remove(e)}
                      className="h-8 w-8 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="extension-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Extension" : "New Extension"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Number</Label>
                <Input data-testid="extension-number-input" value={form.number} required
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638] font-mono" placeholder="101" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Label</Label>
                <Input data-testid="extension-label-input" value={form.label} required
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638]" placeholder="Unit 4B" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="extension-kind-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    <SelectItem value="tenant">Tenant (receives calls)</SelectItem>
                    <SelectItem value="intercom">Intercom (door station)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Server</Label>
                <Select value={form.server_id} onValueChange={(v) => setForm({ ...form, server_id: v })}>
                  <SelectTrigger data-testid="extension-server-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue placeholder="Select server" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    {servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.domain})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Site</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                  <SelectTrigger data-testid="extension-site-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.kind === "intercom" && (
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Door Relay URL (optional)</Label>
                  <Input data-testid="extension-unlock-url-input" value={form.unlock_url}
                    onChange={(e) => setForm({ ...form, unlock_url: e.target.value })}
                    className="bg-[#0A0D14] border-[#1E2638] font-mono" placeholder="http://192.168.1.50/open?token=abc" />
                  <p className="text-xs text-slate-500">Called (HTTP GET) when a tenant presses Unlock — wire it to your intercom's relay/API.</p>
                </div>
              )}
              {form.kind === "intercom" && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-300">DTMF Unlock Digit (optional)</Label>
                    <Select value={form.dtmf_digit || "none"}
                      onValueChange={(v) => setForm({ ...form, dtmf_digit: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="extension-dtmf-digit-select" className="bg-[#0A0D14] border-[#1E2638]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                        <SelectItem value="none">Disabled</SelectItem>
                        {["0","1","2","3","4","5","6","7","8","9","*","#"].map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">DTMF Type</Label>
                    <Select value={form.dtmf_type} onValueChange={(v) => setForm({ ...form, dtmf_type: v })}>
                      <SelectTrigger data-testid="extension-dtmf-type-select" className="bg-[#0A0D14] border-[#1E2638]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                        <SelectItem value="rfc2833">RFC2833 (RTP event — most common)</SelectItem>
                        <SelectItem value="sip_info">SIP INFO</SelectItem>
                        <SelectItem value="inband">Inband audio</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Must match your intercom's DTMF setting. The tenant's Unlock button sends this tone during calls; softphone users can also press the digit on their keypad.</p>
                  </div>
                </>
              )}
              {form.kind === "tenant" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Assign To User</Label>
                  <Select value={form.assigned_user_id}
                    onValueChange={(v) => setForm({ ...form, assigned_user_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="extension-user-select" className="bg-[#0A0D14] border-[#1E2638]">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.username} (@{u.username})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Button type="submit" data-testid="extension-submit-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {editing ? "Save Changes" : "Create Extension"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrExt} onOpenChange={() => setQrExt(null)}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="qr-dialog">
          <DialogHeader><DialogTitle>Softphone QR — Ext {qrExt?.number}</DialogTitle>
            <DialogDescription className="text-slate-400">Scan with your SIP app to provision this extension, then enter the password shown.</DialogDescription>
          </DialogHeader>
          {qrExt && (
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white p-4 rounded-xl" data-testid="qr-code-image">
                <QRCode value={qrValue(qrExt)} size={200} />
              </div>
              <div className="w-full bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3 space-y-1 text-sm font-mono">
                <div className="text-slate-400">username: <span className="text-emerald-400">{qrExt.sip_endpoint || qrExt.number}</span></div>
                <div className="text-slate-400">password: <span className="text-emerald-400">{qrExt.sip_secret}</span></div>
                <div className="text-slate-400">server: <span className="text-emerald-400">{serverOf(qrExt.server_id)}</span></div>
              </div>
              <p className="text-xs text-slate-500 text-center">
                Scan with Linphone / Zoiper (or any SIP app that accepts sip: URIs), then enter the password shown.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="batch-dialog">
          <DialogHeader><DialogTitle>Batch Create Extensions</DialogTitle>
            <DialogDescription className="text-slate-400">Create a range of extensions for one site and server. Existing numbers are skipped.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBatch} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">From number</Label>
                <Input data-testid="batch-start-input" type="number" value={batchForm.start} required
                  onChange={(e) => setBatchForm({ ...batchForm, start: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638] font-mono" placeholder="101" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">To number</Label>
                <Input data-testid="batch-end-input" type="number" value={batchForm.end} required
                  onChange={(e) => setBatchForm({ ...batchForm, end: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638] font-mono" placeholder="120" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-300">Label pattern</Label>
                <Input data-testid="batch-label-input" value={batchForm.label_pattern} required
                  onChange={(e) => setBatchForm({ ...batchForm, label_pattern: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638]" placeholder="Unit {n} — {n} is replaced with the number" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Server</Label>
                <Select value={batchForm.server_id} onValueChange={(v) => setBatchForm({ ...batchForm, server_id: v })}>
                  <SelectTrigger data-testid="batch-server-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue placeholder="Select server" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    {servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.domain})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Site</Label>
                <Select value={batchForm.site_id} onValueChange={(v) => setBatchForm({ ...batchForm, site_id: v })}>
                  <SelectTrigger data-testid="batch-site-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" data-testid="batch-submit-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              Create Range
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
